import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { PlanFeature } from '@models/asset-details.model';
import { createShoppingObject } from '@models/functions.model';
import { CartFeatureItem, ManageAddOnFeatures } from '@models/manage-features.model';
import { ShoppingCartData, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  ADDED_FEATURE,
  CART_ITEM_TYPE_FEATURE,
  FeatureType,
  REMOVED_FEATURE,
  UNCHANGED_FEATURE
} from '@models/shopping.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { BaseBadgeComponent } from '@shared-components/badge/base-badge/base-badge.component';
import { ViewDetailsComponent } from '@shared-components/view-details/view-details.component';
import { Subscription } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

@Component({
  selector: 'ts-add-or-remove-features',
  templateUrl: './add-or-remove-features.component.html',
  imports: [
    CommonModule,
    TranslationPipe,
    AsButtonDirective,
    DataLoaderDirective,
    StopClickPropagationDirective,
    BaseBadgeComponent
  ]
})
export class AddOrRemoveFeaturesComponent implements OnChanges, OnDestroy {
  @Input() shopping_cart: ShoppingCartData;
  @Output() emit_cart_features: EventEmitter<ShoppingCartItem[]> = new EventEmitter();
  add_on_features: Array<FeatureType | PlanFeature>;
  added_features: Array<FeatureType | PlanFeature | ShoppingCartItem> = [];
  current_features: Array<FeatureType | PlanFeature | ShoppingCartItem> = [];
  no_features: boolean;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shopping_cart'] && changes['shopping_cart'].currentValue) this.getAddOnFeatures();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFeatureName = (feature: FeatureType | PlanFeature): string =>
    ((feature as FeatureType).name_override || feature.name) as string;

  currentFeature = (feature: FeatureType | PlanFeature): boolean =>
    Boolean(this.current_features.find((f) => f.id == feature.id));

  addedFeature = (feature: FeatureType | PlanFeature): boolean =>
    Boolean(this.added_features.find((f) => f.id == feature.id));

  includedFeature = (feature: FeatureType | PlanFeature): boolean =>
    (feature as FeatureType).included ||
    ((feature as PlanFeature).checked && (feature as PlanFeature).locked)!;

  existingFeature = (feature: FeatureType | PlanFeature): boolean =>
    Boolean(this.shopping_cart.existing_asset?.line.plan.features?.find((f) => f.id == feature.id));

  removedFeature = (feature: FeatureType | PlanFeature): boolean =>
    Boolean(this.existingFeature(feature) && !this.currentFeature(feature));

  manageFeatures(feature: FeatureType | PlanFeature): void {
    let sub_type: CartFeatureItem['sub_type'] | undefined = undefined;
    if (this.includedFeature(feature)) {
      this.alertIncludedFeature(feature as FeatureType);
      return;
    }

    if (this.existingFeature(feature)) {
      if (!this.currentFeature(feature)) this.current_features.push(feature);
      else this.current_features = this.current_features.filter((f) => f.id != feature.id);

      sub_type = !this.currentFeature(feature) ? REMOVED_FEATURE : UNCHANGED_FEATURE;
    } else {
      if (!this.addedFeature(feature)) this.added_features.push(feature);
      else this.added_features = this.added_features.filter((f) => f.id != feature.id);

      sub_type = !this.addedFeature(feature) ? undefined : ADDED_FEATURE;
    }

    this.buildCartItems(feature, sub_type);
  }

  alertIncludedFeature(f: FeatureType): void {
    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true
    });
    modalRef.componentInstance.modal_title = this.translation_svc.translate('included_feature');
    modalRef.componentInstance.modal_body = `<strong>${f.name}</strong> 
    ${this.translation_svc.translate('included_feature_desc')}`;
  }

  viewDetails(f: FeatureType | PlanFeature): void {
    const modalRef: NgbModalRef = this.modal_svc.open(ViewDetailsComponent, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      modalDialogClass: 'scrollable-modal-maxheight'
    });
    modalRef.componentInstance.modal_title = f.name;
    modalRef.componentInstance.modal_body = f.description;
  }

  private getAddOnFeatures(): void {
    this.subscriptions.add(
      this.shopping_svc
        .getFeatures(this.shopping_cart.carrier?.id!, { lineId: this.shopping_cart.lineId! })
        .subscribe({
          next: (res: ManageAddOnFeatures) => {
            this.setFeatures(res);
            this.add_on_features = [
              ...(this.shopping_cart.existing_asset?.line.plan?.features
                ? [
                    ...this.shopping_cart.existing_asset?.line.plan?.features
                      .filter((x) => x.checked)
                      .sort((a, b) => (a.name! < b.name! ? -1 : 1)),
                    ...this.shopping_cart.existing_asset?.line.plan?.features
                      .filter((x) => !x.checked)
                      .sort((a, b) => (a.name! < b.name! ? -1 : 1))
                  ]
                : []),

              ...(res.features.length ? res.features.filter((x) => x.included) : []).sort((a, b) =>
                a.name < b.name ? -1 : 1
              ),

              ...(res.features.length ? res.features.filter((x) => !x.included) : []).sort(
                (a, b) => (a.name < b.name ? -1 : 1)
              )
            ];
            this.no_features = this.add_on_features.length === 0;
          },
          error: (err: HttpErrorResponse) => {}
        })
    );
  }

  private setFeatures(res: ManageAddOnFeatures): void {
    if (this.shopping_cart.shopping_cart_items.length) {
      this.current_features = this.shopping_cart.shopping_cart_items.filter(
        (f) => f.sub_type === UNCHANGED_FEATURE
      );
      this.added_features = this.shopping_cart.shopping_cart_items.filter(
        (f) => f.sub_type === ADDED_FEATURE
      );
      this.emit_cart_features.emit(this.shopping_cart.shopping_cart_items);
      return;
    }

    this.current_features = this.shopping_cart.existing_asset?.line?.plan?.features
      ? [...this.shopping_cart.existing_asset?.line?.plan?.features]
      : [];
    this.added_features = [...(res.features.length ? res.features.filter((x) => x.included) : [])];
    this.current_features.length &&
      this.current_features.forEach((f) => this.buildCartItems(f, UNCHANGED_FEATURE));
    this.added_features.length &&
      this.added_features.forEach((f) => this.buildCartItems(f, ADDED_FEATURE));
  }

  private buildCartItems(
    feature: FeatureType | PlanFeature | ShoppingCartItem,
    sub_type: CartFeatureItem['sub_type'] | undefined
  ): void {
    if (sub_type) {
      const index = this.shopping_cart.shopping_cart_items.findIndex((f) => f.id == feature.id);
      if (index > -1 && [REMOVED_FEATURE, UNCHANGED_FEATURE].includes(sub_type)) {
        this.shopping_cart.shopping_cart_items[index].sub_type = sub_type;
      } else {
        this.shopping_cart.shopping_cart_items.push({
          ...createShoppingObject(
            feature,
            CART_ITEM_TYPE_FEATURE,
            this.shopping_cart.carrier?.image_url
          ),
          description: feature.description,
          sub_type
        });
      }
    } else
      this.shopping_cart.shopping_cart_items = this.shopping_cart.shopping_cart_items.filter(
        (f) => f.id != feature.id
      );

    this.emit_cart_features.emit(this.shopping_cart.shopping_cart_items);
  }
}
